import React, { useEffect, useState } from 'react';
import { Box, Alert, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { fetchWrapper } from '@/lib/fetch-wrapper';
import { urls } from '@/urls';
import { Loader } from '@/components/loader/loader';
import { CostPerDay } from '@cloudcost/shared-types/costs';
import { getUser } from '@/lib/common';
import { fetchAllSpecialPermissions } from '@/lib/permissions';
import { AppCard } from '../app-card/app-card';
import { SvgLineChart, CHART_H } from '@/components/ds/charts';
import { formatCurrencyNoUnit } from '@/lib/formatters';

interface CostPerDayChartProps {
  filters?: string;
  className?: string;
  hideAppCard?: boolean;
  url?: string;
  title?: string;
  subtitle?: string;
  // When embedded (hideAppCard) and the parent renders the legend in its header,
  // suppress the in-body legend (used by Reporting's Total Spending card).
  hideLegend?: boolean;
}

interface UserRoleInfo {
  isSponsor: boolean;
  isAdmin: boolean;
  isDepartmentOwner: boolean;
  isSystemOwner: boolean;
  isCloudAccountOwner: boolean;
  isResourceOwner: boolean;
}

interface ChartConfig {
  title: string;
  showBudget: boolean;
  budgetLabel: string;
  legendItems: string[];
}

const CostPerDayChart = ({
  filters,
  className = 'card100',
  url = urls.DevOps.getCostPerDay,
  hideAppCard = false,
  title,
  subtitle,
  hideLegend = false,
}: CostPerDayChartProps) => {
  const [chartItems, setChartItems] = useState<CostPerDay[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<any>(null);
  const [userRole, setUserRole] = useState<UserRoleInfo | null>(null);
  const [chartConfig, setChartConfig] = useState<ChartConfig | null>(null);

  // Single effect: resolve role/config then fetch cost-per-day once per filters change (avoids double fetch from chained useEffects).
  useEffect(() => {
    let cancelled = false;

    const loadChartData = async () => {
      const user = getUser();
      if (!user) {
        if (!cancelled) {
          setError(new Error('User not found'));
          setLoading(false);
        }
        return;
      }

      const isSponsor = user.is_sponsor;
      const isAdmin = user.is_admin;

      const specialPermissions = await fetchAllSpecialPermissions(user.id);
      if (cancelled) return;

      const roleInfo: UserRoleInfo = {
        isSponsor,
        isAdmin,
        isDepartmentOwner: specialPermissions.isDepartmentOwner,
        isSystemOwner: specialPermissions.isSystemOwner,
        isCloudAccountOwner: specialPermissions.isCloudAccountOwner,
        isResourceOwner: specialPermissions.isResourceOwner,
      };

      let config: ChartConfig;

      if (isSponsor || isAdmin) {
        config = {
          title: 'Global spending in current and previous month',
          showBudget: true,
          budgetLabel: 'Global Budget',
          legendItems: [
            'Current Month',
            'Last Month',
            'Forecast',
            'Global Budget',
          ],
        };
      } else if (specialPermissions.isDepartmentOwner) {
        config = {
          title:
            'Spending in current and previous month for owned Department(s)',
          showBudget: true,
          budgetLabel: 'Department(s) Budget',
          legendItems: [
            'Current Month',
            'Last Month',
            'Forecast',
            'Department(s) Budget',
          ],
        };
      } else if (specialPermissions.isSystemOwner) {
        config = {
          title: 'Spending in current and previous month for owned System(s)',
          showBudget: true,
          budgetLabel: 'System(s) Budget',
          legendItems: [
            'Current Month',
            'Last Month',
            'Forecast',
            'System(s) Budget',
          ],
        };
      } else if (specialPermissions.isCloudAccountOwner) {
        config = {
          title:
            'Spending in current and previous month for owned Cloud Account(s)',
          showBudget: false,
          budgetLabel: '',
          legendItems: ['Current Month', 'Last Month', 'Forecast'],
        };
      } else if (specialPermissions.isResourceOwner) {
        config = {
          title: 'Spending in current and previous month for owned resources',
          showBudget: false,
          budgetLabel: '',
          legendItems: ['Current Month', 'Last Month', 'Forecast'],
        };
      } else {
        config = {
          title: 'Spending in current and previous month',
          showBudget: false,
          budgetLabel: '',
          legendItems: ['Current Month', 'Last Month', 'Forecast'],
        };
      }

      // Hide budget when resource filter is applied
      if (filters && filters.includes('resource=')) {
        config.showBudget = false;
        config.budgetLabel = '';
        config.legendItems = config.legendItems.filter(
          (item) => item !== 'Global Budget' && item !== 'Department(s) Budget' && item !== 'System(s) Budget',
        );
      }

      if (cancelled) return;
      setUserRole(roleInfo);
      setChartConfig(config);

      setLoading(true);
      try {
        const data = await fetchWrapper.get(url + (filters ? `?${filters}` : ''));
        if (!cancelled) {
          setChartItems(data);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('There was an error!', err);
          setError(err);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadChartData();
    return () => {
      cancelled = true;
    };
  }, [filters, url]);

  if (loading || !userRole || !chartConfig) {
    return <Loader />;
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">Error loading cost data: {error.message}</Alert>
      </Box>
    );
  }

  const chartData = chartItems.map((item: any) => ({
    date: new Date(item.date_created_formated.split('-').join('-')),
    spending:
      item.cost !== null && item.cost !== undefined ? Number(item.cost) : null,
    forecast:
      item.forecastperday !== null && item.forecastperday !== undefined
        ? Number(item.forecastperday)
        : null,
    budget:
      item.globalbudget !== null && item.globalbudget !== undefined
        ? Number(item.globalbudget)
        : null,
    lastMonth:
      item.costlastmonth !== null && item.costlastmonth !== undefined
        ? Number(item.costlastmonth)
        : null,
  }));

  // Index of the last real "actual" point — the forecast continues from here.
  let lastActualIndex = -1;
  chartData.forEach((item, i) => {
    if (item.spending !== null && item.spending !== undefined) {
      lastActualIndex = i;
    }
  });

  // Current-month cumulative daily series (actual), carrying forward past any gaps.
  const actual: number[] = [];
  for (let i = 0; i <= lastActualIndex; i++) {
    const s = chartData[i].spending;
    actual.push(
      s === null || s === undefined
        ? actual.length
          ? actual[actual.length - 1]
          : 0
        : s,
    );
  }

  // Forecast series continuing from the last actual point (overlap point first).
  const forecast: number[] = [];
  if (lastActualIndex >= 0) {
    forecast.push(actual[actual.length - 1]);
    for (let i = lastActualIndex + 1; i < chartData.length; i++) {
      const f = chartData[i].forecast;
      if (f !== null && f !== undefined) forecast.push(f);
    }
  }

  // Previous month's cumulative daily spend, aligned by day-of-month (full-month
  // overlay, indexed from slot 0 — unlike forecast, no offset).
  const lastMonth = chartData.map((item) => item.lastMonth);

  // Budget reference line (first non-null budget value) when the role shows budget.
  let budgetLine: number | undefined;
  if (chartConfig.showBudget) {
    const b = chartData.find(
      (item) => item.budget !== null && item.budget !== undefined,
    );
    if (b && b.budget !== null && b.budget !== undefined) budgetLine = b.budget;
  }

  // X labels: evenly-spaced 'MMM D' date labels across the full month width.
  const allLabels = chartData.map((item) => dayjs(item.date).format('MMM D'));
  const N = allLabels.length;
  const targetTicks = Math.min(N, 8);
  const xLabels =
    N > 0
      ? Array.from({ length: targetTicks }, (_, j) =>
          allLabels[
            Math.round((j * (N - 1)) / Math.max(1, targetTicks - 1))
          ],
        )
      : [];

  const legendItem = (
    label: string,
    swatch: React.CSSProperties,
  ) => (
    <span
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 5,
        color: '#5A6472',
      }}>
      <span style={{ display: 'inline-block', ...swatch }} />
      {label}
    </span>
  );

  const cardLegend = (
    <div
      style={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 14,
        fontSize: 12,
        fontWeight: 700,
      }}>
      {legendItem('Actual', {
        width: 14,
        height: 3,
        borderRadius: 2,
        background: '#1C71E3',
      })}
      {legendItem('Last Month', {
        width: 14,
        height: 3,
        borderRadius: 2,
        background: '#4E79A7',
      })}
      {legendItem('Forecast', {
        width: 14,
        height: 3,
        borderRadius: 2,
        background: '#F28E2C',
      })}
      {chartConfig.showBudget &&
        legendItem(chartConfig.budgetLabel || 'Budget', {
          width: 14,
          height: 0,
          borderTop: '2px solid #E23D3D',
        })}
    </div>
  );

  const Chart = () => (
    <Box sx={{ position: 'relative', width: '100%' }}>
      <SvgLineChart
        actual={actual}
        forecast={forecast}
        lastMonth={lastMonth}
        budgetLine={budgetLine}
        totalSlots={Math.max(1, chartData.length - 1)}
        h={CHART_H}
        fmt={formatCurrencyNoUnit}
        xLabels={xLabels}
        pointLabels={allLabels}
        budgetLabel={chartConfig.budgetLabel}
        seriesLabels={{
          actual: 'Current Month',
          lastMonth: 'Last Month',
          forecast: 'Forecast',
          budget: chartConfig.budgetLabel,
        }}
      />
    </Box>
  );

  if (hideAppCard) {
    return (
      <Box sx={{ width: '100%' }}>
        {!hideLegend && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
            {cardLegend}
          </Box>
        )}
        <Chart />
      </Box>
    );
  }

  return (
    <AppCard
      key="total-spending-card"
      id="total-spending-card"
      className={className}
      title={title ?? chartConfig.title}
      intro=""
      draggable
      headerRight={cardLegend}>
      {subtitle && (
        <Typography
          sx={{
            fontFamily: 'Lato',
            fontSize: 12.5,
            color: '#8A94A6',
            mb: 1,
          }}>
          {subtitle}
        </Typography>
      )}
      <Chart />
    </AppCard>
  );
};

export default CostPerDayChart;
